import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type Mark, updateMergeTagText } from '@shared/dream-components';
import { produce } from 'immer';

import { getProductTypeLabel } from '~/constants/productTypes';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import { type Product, WEBINAR_REDEEMABLE_STATUS } from '~/interfaces/product';
import type { StaticPageLoaderData } from '~/services/static_page';

import { serializeNode } from '../serializeNode';
import type { NodeWithMergeTag } from './types';

// Strikethrough price styling constants
// Use theme primary color with 50% opacity (fallback to gray if theme not available)
const getStrikethroughColor = (customColor?: string): string => {
  if (customColor) return customColor;
  // Use CSS variable with color-mix for theme primary at 50% opacity
  // --wt-primary-color is set in Orchid apps (see app/views/web_themes/_stylesheet.css.erb)
  return 'color-mix(in srgb, var(--wt-primary-color, #6B7280) 50%, transparent)';
};

// Calculate strikethrough font size as 3/4 of the current font size
const calculateStrikethroughFontSize = (currentFontSize?: string | number): string => {
  if (!currentFontSize) {
    // Default fallback if no font size is available
    return '12px';
  }

  // Parse the font size (could be "16px", "1rem", "1.5em", etc.)
  const fontSizeStr = String(currentFontSize);
  const match = fontSizeStr.match(/^([\d.]+)([a-z%]+)?$/i);

  if (!match) {
    return '12px'; // Fallback
  }

  const value = Number.parseFloat(match[1]);
  const unit = match[2] || 'px';
  const calculatedValue = value * 0.75; // 3/4 = 0.75

  return `${calculatedValue}${unit}`;
};

const getBuyActionButtonText = (product: Product, t: (key: string) => string): string => {
  const webinarRedeemable = product.default_product_variant?.redeemable;
  const isWebinar = webinarRedeemable?.type === 'ProductRedeemables::Webinar';

  if (product.status === 'archived' || product.sold_out) {
    return t('products.cta.sold_out');
  }

  if (isWebinar && webinarRedeemable?.status === WEBINAR_REDEEMABLE_STATUS.CANCELLED) {
    return t('products.cta.cancelled');
  }

  if (isWebinar && webinarRedeemable?.status === WEBINAR_REDEEMABLE_STATUS.ENDED) {
    return t('products.cta.ended');
  }

  return product.cta_text || '';
};

const ALLOWED_MERGE_TAGS = [
  'product.name',
  'product.description',
  'product.details',
  'product.price',
  'product.product_type',
  'product.image',
  'product.buy_action',
] as const;

type AllowedMergeTag = (typeof ALLOWED_MERGE_TAGS)[number];

interface UseProductMergeTagProcessorProps {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

interface ProcessedResult {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

const useProductMergeTagProcessor = ({ node, children }: UseProductMergeTagProcessorProps): ProcessedResult => {
  const { t } = useTranslation();
  const { product } = useTypedLoaderData<
    StaticPageLoaderData & {
      product?: Product;
    }
  >();

  return useMemo(() => {
    const mergeTag = node.attrs?.mergeTag;

    // Early return if no merge tag or not an allowed merge tag
    if (!mergeTag || !ALLOWED_MERGE_TAGS.includes(mergeTag as AllowedMergeTag)) {
      return { node, children };
    }

    // Early return if no product present
    if (!product) {
      return { node, children };
    }

    let elementToRender: NodeWithMergeTag = node;

    switch (mergeTag) {
      case 'product.name':
        elementToRender = updateMergeTagText(node, product.name || '') as NodeWithMergeTag;
        break;
      case 'product.description':
        elementToRender = updateMergeTagText(node, product.description || '') as NodeWithMergeTag;
        break;
      case 'product.price': {
        // Show "Free" for free products
        if (product.free) {
          elementToRender = updateMergeTagText(node, t('products.free_label')) as NodeWithMergeTag;
          break;
        }

        const variant = product.default_product_variant;
        const currentPrice = variant?.price || '';
        const originalPrice = variant?.original_price;

        // If original price exists and is greater than current price, show both
        if (
          originalPrice &&
          variant?.original_unit_amount &&
          variant?.unit_amount &&
          variant.original_unit_amount > variant.unit_amount
        ) {
          // Get the current font size from the original node's content before modifying
          // Check the first text node in the original node's content for fontSize
          const originalTextNode = node.content?.[0];
          let currentFontSize: number | string | undefined;
          if (originalTextNode && typeof originalTextNode === 'object' && 'marks' in originalTextNode) {
            const marks = originalTextNode.marks;
            if (Array.isArray(marks)) {
              const textStyleMark = (marks as Mark[]).find((m) => m.type === 'textStyle');
              currentFontSize = textStyleMark?.attrs?.fontSize;
            }
          }

          // Create a node with both prices: current + original (strikethrough)
          const baseNode = updateMergeTagText(node, `${currentPrice} ${originalPrice}`);
          elementToRender = produce(baseNode, (draft) => {
            if (draft.content?.[0]?.type === 'text' && draft.content[0].text) {
              const text = draft.content[0].text;
              const currentPriceLength = currentPrice.length;

              // Split the text into current price and original price
              const currentPriceText = text.substring(0, currentPriceLength);
              const originalPriceText = text.substring(currentPriceLength + 1); // +1 for space

              // Preserve existing marks from the node if any (excluding strike to avoid duplicates)
              const existingMarks = (draft.content?.[0]?.marks || []).filter((m) => m.type !== 'strike');

              // Get strikethrough styling from paragraph or heading attributes, with defaults
              // Calculate strikethrough font size as 3/4 of current font size
              const strikeAttrs: Record<string, string> = {};
              if ((node.type === 'paragraph' || node.type === 'heading') && node.attrs) {
                const nodeAttrs = node.attrs;
                const customColor = (nodeAttrs.strikethroughColor as string)?.trim();
                strikeAttrs.strikethroughColor = customColor || getStrikethroughColor();
                // Calculate strikethrough font size as 3/4 of current font size
                strikeAttrs.strikethroughFontSize = calculateStrikethroughFontSize(currentFontSize);
                // Use regular font weight for original price
                strikeAttrs.strikethroughFontWeight = 'normal';
              } else {
                // Set defaults if not in paragraph/heading
                strikeAttrs.strikethroughColor = getStrikethroughColor();
                strikeAttrs.strikethroughFontSize = calculateStrikethroughFontSize(currentFontSize);
                strikeAttrs.strikethroughFontWeight = 'normal';
              }

              // Create two text nodes: current price first, then original price with strike mark
              draft.content = [
                {
                  type: 'text',
                  text: `${currentPriceText} `,
                  marks: existingMarks,
                },
                {
                  type: 'text',
                  text: originalPriceText,
                  marks: [
                    ...existingMarks,
                    {
                      type: 'strike',
                      attrs: strikeAttrs,
                    },
                  ],
                },
              ];
            }
          }) as NodeWithMergeTag;
        } else {
          // No original price, just show current price
          elementToRender = updateMergeTagText(node, currentPrice) as NodeWithMergeTag;
        }
        break;
      }
      case 'product.product_type':
        elementToRender = updateMergeTagText(node, getProductTypeLabel(product.product_type)) as NodeWithMergeTag;
        break;
      case 'product.details':
        elementToRender = {
          ...node,
          attrs: {
            ...node.attrs,
            tiptapState: product.tiptap_state,
          },
        } as NodeWithMergeTag;
        break;
      case 'product.image': {
        const assets = product.default_product_variant?.assets || [];
        const primaryAsset = assets[0];
        const primaryImageUrl = primaryAsset?.url || product.default_product_variant?.default_image_url || '';
        const imageAttrs = node.attrs as Record<string, unknown>;

        if (assets.length > 1) {
          // Multiple images - enable carousel
          const secondaryImages = assets.slice(1).map((asset) => ({
            url: asset.url,
            alt: asset.alt_text || '',
          }));

          elementToRender = {
            ...node,
            attrs: {
              ...node.attrs,
              alt: primaryAsset?.alt_text || product.name || '',
              src: primaryImageUrl,
              secondaryImages,
              carouselVariant: imageAttrs.carouselVariant || 'thumbnails-bottom',
              galleryLayout: imageAttrs.galleryLayout || 'carousel',
            },
          } as NodeWithMergeTag;
        } else {
          // Single image - no carousel
          elementToRender = {
            ...node,
            attrs: {
              ...node.attrs,
              alt: primaryAsset?.alt_text || product.name || '',
              src: primaryImageUrl,
            },
          } as NodeWithMergeTag;
        }
        break;
      }
      case 'product.buy_action': {
        const buttonText = getBuyActionButtonText(product, t);
        elementToRender = updateMergeTagText(node, buttonText) as NodeWithMergeTag;
        break;
      }
      default:
        elementToRender = node;
    }

    const childrenToRender = elementToRender.content ? serializeNode(elementToRender.content) : children;

    return {
      node: elementToRender,
      children: childrenToRender,
    };
  }, [node, children, product, t]);
};

export default useProductMergeTagProcessor;
